import { useCallback } from "react"
import { useAddReviewDialog } from "@/hooks/use-add-review-dialog"
import { showDialogInfo } from "@/hooks/use-dialog-info"
import { PlaceReview } from "@/types/place"

import { useDeletePlaceReviewMutation } from "@/lib/storage/api/place-api"
import { toastManager } from "@/lib/toast"
import IconEllipsis from "@/components/icons/ellipsis"
import IconPencil from "@/components/icons/pencil"
import IconTrash from "@/components/icons/trash"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { IconButton } from "@/components/ui/icon-button"

interface ReviewActionsMenuProps {
  review: PlaceReview
  triggerClassName?: string
  onDeleted?: (reviewId: number) => void
}

export function ReviewActionsMenu({
  review,
  triggerClassName,
  onDeleted,
}: ReviewActionsMenuProps) {
  const [deletePlaceReview, { isLoading: isDeleting }] =
    useDeletePlaceReviewMutation()
  const { openAddReviewDialog, AddReviewDialog } = useAddReviewDialog()

  const canEditReview = review.is_editable !== false

  const handleEdit = useCallback(() => {
    if (!review.place?.entity_ref || !review.place?.type) {
      toastManager.add({
        type: "error",
        description: "Cannot edit this review right now.",
      })
      return
    }

    openAddReviewDialog({
      placeRef: review.place.entity_ref,
      placeType: review.place.type,
      initialReview: review,
    })
  }, [openAddReviewDialog, review])

  const handleDelete = useCallback(async () => {
    if (!review.place?.entity_ref) {
      toastManager.add({
        type: "error",
        description: "Cannot delete this review right now.",
      })
      return
    }

    try {
      await deletePlaceReview({
        placeRef: review.place.entity_ref,
        reviewId: review.id,
      }).unwrap()
      toastManager.add({ type: "success", description: "Review deleted" })
      onDeleted?.(review.id)
    } catch {
      toastManager.add({
        type: "error",
        description: "Could not delete review. Please try again.",
      })
    }
  }, [deletePlaceReview, onDeleted, review])

  const handleDeleteConfirm = useCallback(() => {
    if (!review.place?.entity_ref) {
      toastManager.add({
        type: "error",
        description: "Cannot delete this review right now.",
      })
      return
    }

    showDialogInfo({
      title: "Delete review?",
      message: "This action cannot be undone.",
      okLabel: "Delete",
      cancelLabel: "Cancel",
      okVariant: "destructive",
      okAction: () => {
        void handleDelete()
      },
    })
  }, [handleDelete, review])

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <IconButton
              icon={IconEllipsis}
              variant="outline"
              size="xs"
              label="Review actions"
              className={triggerClassName}
            />
          }
        />
        <DropdownMenuContent>
          <DropdownMenuItem
            icon={IconPencil}
            onClick={handleEdit}
            disabled={!canEditReview}
          >
            Edit
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            icon={IconTrash}
            variant="destructive"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {AddReviewDialog}
    </>
  )
}
